/**
 * A schedule day is cut into blocks of 15 minutes, starting at 5:00 AM. A normal day runs 96 blocks, to 5:00 AM the
 * next morning (so a late close past midnight still belongs to the day it started on). An "Overnight party" day
 * (owner 10/23) can run on to 9:00 AM, so every stored row has 112 blocks; on a normal day the last 16 stay off.
 *
 * Block 0 = 5:00–5:15 AM, block 24 = 11:00 AM, block 68 = 10:00 PM, block 95 = 4:45 AM, block 111 = 8:45 AM.
 */
export const BLOCKS_PER_HOUR = 4;
export const BLOCK_MINUTES = 15;
export const FIRST_HOUR = 5;
export const DAY_HOUR = 29; // a normal day ends at 5 AM the next morning …
export const LAST_HOUR = 33; // … an overnight party at 9 AM at the latest
/** Blocks in a stored row (5 AM to 9 AM next morning). */
export const BLOCKS_PER_DAY = (LAST_HOUR - FIRST_HOUR) * BLOCKS_PER_HOUR; // 112
/** Where a normal day ends (5 AM). */
export const DAY_BLOCKS = (DAY_HOUR - FIRST_HOUR) * BLOCKS_PER_HOUR; // 96
/** The end times an overnight party can have: 5:30 AM … 9:00 AM, every 30 minutes. */
export const OVERNIGHT_ENDS = Array.from({ length: (BLOCKS_PER_DAY - DAY_BLOCKS) / 2 }, (_, i) => DAY_BLOCKS + 2 + 2 * i);
/** A row in the stored length: an older 96-block row gets the overnight blocks added (off). */
export function fullRow(row) {
    return row.length >= BLOCKS_PER_DAY ? row.slice(0, BLOCKS_PER_DAY) : row + '.'.repeat(BLOCKS_PER_DAY - row.length);
}
export const BLOCK_KINDS = ['.', 'W', 'B', 'L'];
export function emptyRow() {
    return '.'.repeat(BLOCKS_PER_DAY);
}
export function isValidRow(row) {
    return (row.length === BLOCKS_PER_DAY || row.length === DAY_BLOCKS) && /^[.WBL]+$/.test(row);
}
/** Minutes after midnight (can exceed 1440 for after-midnight blocks). */
export function blockStartMinutes(block) {
    return FIRST_HOUR * 60 + block * BLOCK_MINUTES;
}
/** "11:00a", "3:15p", "12:30a" — the short form used on the boards. */
export function blockLabel(block) {
    const m = blockStartMinutes(block);
    const h = Math.floor(m / 60) % 24;
    const mm = m % 60;
    const ap = h >= 12 ? 'p' : 'a';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${mm < 10 ? '0' : ''}${mm}${ap}`;
}
/** "11 AM", "3 PM" for an hour number (5–28). */
export function hourLabel(hour) {
    const h = hour % 24;
    const ap = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12} ${ap}`;
}
export function hourOfBlock(block) {
    return FIRST_HOUR + Math.floor(block / BLOCKS_PER_HOUR);
}
export function firstBlockOfHour(hour) {
    return (hour - FIRST_HOUR) * BLOCKS_PER_HOUR;
}
/** Continuous runs of non-off blocks: [start, endExclusive][] */
export function workRuns(row) {
    const runs = [];
    let i = 0;
    while (i < row.length) {
        if (row[i] === '.') {
            i++;
            continue;
        }
        const start = i;
        while (i < row.length && row[i] !== '.')
            i++;
        runs.push([start, i]);
    }
    return runs;
}
/** The row with everything from `fromBlock` on turned off (someone left early / called out). */
export function cutFrom(row, fromBlock) {
    if (fromBlock == null)
        return row;
    const b = Math.max(0, Math.min(BLOCKS_PER_DAY, fromBlock));
    return row.slice(0, b) + '.'.repeat(BLOCKS_PER_DAY - b);
}
/** The row with everything before `toBlock` turned off (someone arrived late, owner 10/22). */
export function cutBefore(row, toBlock) {
    if (toBlock == null)
        return row;
    const b = Math.max(0, Math.min(BLOCKS_PER_DAY, toBlock));
    return '.'.repeat(b) + row.slice(b);
}
/** What actually happened: off before they arrived (late) and from when they left (left early / called out). */
export function actualRow(row, leftAt, lateAt) {
    return cutBefore(cutFrom(row, leftAt), lateAt);
}
export function summarize(row) {
    const runs = workRuns(row);
    if (!runs.length)
        return { shift: 'Off', paidHours: 0, breaks: [] };
    let work = 0;
    let lunch = 0;
    const breaks = [];
    for (let i = 0; i < row.length; i++) {
        const k = row[i];
        if (k !== '.')
            work++;
        if (k === 'L')
            lunch++;
        if (k === 'B')
            breaks.push('B ' + blockLabel(i));
        if (k === 'L' && row[i - 1] !== 'L')
            breaks.push('L ' + blockLabel(i));
    }
    return {
        shift: runs.map(([a, b]) => blockLabel(a) + '–' + blockLabel(b)).join(' & '),
        paidHours: (work - lunch) / BLOCKS_PER_HOUR,
        breaks,
    };
}
//# sourceMappingURL=time.js.map